import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Trophy, Sun, Users, CheckCircle2 } from "lucide-react";

function PillButton({ to, children, variant = "primary", testId }) {
  const base = "inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold transition-all hover:-translate-y-0.5";
  const styles = variant === "primary"
    ? "bg-[#FF9F1C] text-white shadow-[0_12px_30px_rgb(255,159,28,0.35)] hover:bg-[#F08B0E]"
    : "bg-white text-[#1E1E24] border border-slate-200 hover:border-[#FF9F1C] hover:text-[#FF9F1C]";
  return (
    <Link to={to} data-testid={testId} className={`${base} ${styles}`}>
      {children} <ArrowRight className="w-4 h-4" />
    </Link>
  );
}

export default function Home() {
  return (
    <div>
      {/* Hero */}
      <section className="relative isolate overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0 -z-10 pointer-events-none" aria-hidden="true">
          <img src="/assets/competition-group.webp" alt="" fetchPriority="high" className="w-full h-full object-cover object-[center_30%]" data-testid="hero-background-photo" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#FDFFFC]/80 via-[#FFF8E9]/80 to-[#FDFFFC]/95" data-testid="hero-photo-tint" />
        </div>
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#FDE74C]/45 blur-3xl" aria-hidden />
        <div className="absolute -bottom-40 -left-24 w-[520px] h-[520px] rounded-full bg-[#FFBF69]/45 blur-3xl" aria-hidden />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[380px] h-[380px] rounded-full bg-[#FF9F1C]/10 blur-3xl" aria-hidden />

        <div className="max-w-4xl mx-auto px-5 md:px-10 pt-20 md:pt-28 pb-24 text-center relative animate-fade-up" data-testid="hero-content">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs uppercase tracking-wider font-semibold bg-[#FFBF69]/25 text-[#8A5A17]">
            <Sparkles className="w-3.5 h-3.5" /> Annapolis youth cubing club
          </span>
          <h1 className="mt-8 font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02]" data-testid="hero-heading">
            Learn, compete, <br className="hidden sm:block" />and <span className="text-[#FF9F1C]">have fun.</span>
          </h1>
          <p className="relative -top-4 mt-8 text-lg sm:text-xl text-[#51515B] leading-relaxed max-w-2xl mx-auto" data-testid="hero-description">
            Naptown Cubers brings together kids and teens who enjoy solving the Rubik's Cube. Join us for a friendly competition, sign up for summer camp, or come work toward your next personal best.
          </p>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <PillButton to="/competitions" variant="primary" testId="cta-join-competition">Join a Competition</PillButton>
            <PillButton to="/summer-camp" variant="ghost" testId="cta-summer-camp">Summer Camp</PillButton>
            <PillButton to="/contact" variant="ghost" testId="cta-contact">Contact Us</PillButton>
          </div>

          <div className="mt-14 inline-flex flex-wrap items-center gap-6 md:gap-10 text-sm text-[#7A7D8C] justify-center bg-white/70 backdrop-blur-md border border-slate-100 rounded-full px-6 py-3 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <div data-testid="home-category-count"><span className="font-black text-xl text-[#1E1E24] font-display">5</span> speed categories</div>
            <div className="w-px h-6 bg-slate-200 hidden md:block" />
            <div data-testid="home-grade-eligibility"><span className="font-black text-xl text-[#1E1E24] font-display">Grades 3–12</span> competitors</div>
            <div className="w-px h-6 bg-slate-200 hidden md:block" />
            <div><span className="font-black text-xl text-[#1E1E24] font-display">3×3</span> official</div>
          </div>
        </div>
      </section>

      {/* Feature strip */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-16 grid md:grid-cols-3 gap-6">
        <FeatureCard
          icon={<Trophy className="w-6 h-6" />}
          title="Competitions"
          text="Friendly local tournaments where kids get placed into speed categories that fit their pace."
          to="/competitions"
          tid="feature-competitions"
        />
        <FeatureCard
          icon={<Sun className="w-6 h-6" />}
          title="Summer Camp"
          text="A week of cubing, games, and new friends. Beginners through algorithms — everyone belongs."
          to="/summer-camp"
          tid="feature-summer-camp"
        />
        <FeatureCard
          icon={<Users className="w-6 h-6" />}
          title="Sponsors"
          text="Local businesses and families keep our events affordable. Join the crew that makes it happen."
          to="/sponsors"
          tid="feature-sponsors"
        />
      </section>

      {/* Why parents love it */}
      <section className="max-w-5xl mx-auto px-5 md:px-10 py-20 text-center">
        <span className="text-xs uppercase tracking-wider font-semibold text-[#FF9F1C]">Why families love Naptown Cubers</span>
        <h2 className="mt-4 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
          A friendly place for <span className="text-[#FF9F1C]">every cuber.</span>
        </h2>
        <p className="mt-6 text-[#7A7D8C] text-lg leading-relaxed max-w-2xl mx-auto">
          Every child solves at a different speed, and that is completely okay. Before the competition begins, each cuber completes one Timing Solve. We use their time to place them in the category that gives them the fairest and most enjoyable experience.
        </p>

        <ul className="mt-12 grid md:grid-cols-3 gap-5 text-left">
          {[
            { t: "Fair speed categories", d: "Competitors are grouped by their Timing Solve in five categories, from less than 20 seconds through 91–120 seconds." },
            { t: "Parents choose a starting category", d: "Parents select the category they think fits best, and their recorded Timing Solve automatically assigns the competition category." },
            { t: "Personal bests are celebrated", d: "Winning is exciting, but improving, having fun, and reaching a new personal best matter just as much." },
          ].map((r) => (
            <li key={r.t} className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="flex items-start gap-2 text-[#FF9F1C]"><CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" /><span className="font-display font-bold text-lg text-[#1E1E24] leading-snug">{r.t}</span></div>
              <p className="mt-2 text-sm text-[#7A7D8C] leading-relaxed">{r.d}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* CTA banner */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-20">
        <div className="relative overflow-hidden rounded-[36px] bg-[#FF9F1C] p-10 md:p-16 text-white">
          <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-[#FDE74C]/40 blur-2xl" aria-hidden />
          <div className="relative grid md:grid-cols-2 items-center gap-8">
            <div>
              <h3 className="font-display font-black text-3xl sm:text-4xl leading-tight">Ready for your next personal best?</h3>
              <p className="mt-3 text-white/90 max-w-md">Sign your cuber up for our next event, or ask us anything — we love new faces.</p>
            </div>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <Link to="/competitions" data-testid="cta-banner-register" className="px-6 py-3 rounded-full bg-white text-[#B36906] font-bold hover:-translate-y-0.5 transition-transform">Register now</Link>
              <Link to="/contact" data-testid="cta-banner-contact" className="px-6 py-3 rounded-full bg-transparent border border-white/60 text-white font-bold hover:bg-white/10 transition-colors">Ask a question</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function FeatureCard({ icon, title, text, to, tid }) {
  return (
    <Link to={to} data-testid={tid} className="group block bg-white rounded-3xl p-8 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(255,159,28,0.15)] hover:-translate-y-1 transition-transform">
      <div className="w-12 h-12 rounded-2xl bg-[#FFBF69]/25 grid place-items-center text-[#B36906] group-hover:bg-[#FF9F1C] group-hover:text-white transition-colors">{icon}</div>
      <h3 className="mt-5 font-display font-bold text-xl">{title}</h3>
      <p className="mt-2 text-sm text-[#7A7D8C] leading-relaxed">{text}</p>
      <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#FF9F1C]">Learn more <ArrowRight className="w-4 h-4" /></span>
    </Link>
  );
}
